import { useQuery, UseQueryResult } from "@tanstack/react-query";

import { useDealActions } from "../../store";
import { IDeal, IDealRoundsWithInvestors, IInvestor, Nullable, TaggerDocumentData } from "../../store/types";
import { useTaggerQueries } from "../query-context";

export const useGetDealByIdQuery = (dealId: Nullable<IDeal["id"]>): UseQueryResult<Nullable<IDeal>> => {
  const { getDealById } = useTaggerQueries();
  return useQuery(["deal", dealId], () => {
    if (!dealId) {
      return Promise.resolve(null);
    }
    return getDealById(dealId);
  });
};

export const useGetDocumentDataQuery = (docId: string): UseQueryResult<TaggerDocumentData | null> => {
  // const { getDocumentData } = useTaggerQueries();

  return useQuery(["tagger-document", docId], () => Promise.resolve(null));
};

export const useGetDealsQuery = () => {
  const { getDeals } = useTaggerQueries();

  return useQuery(["deals"], async () => {
    const deals = await getDeals();
    deals.sort((d1, d2) => d1.title.localeCompare(d2.title));
    return deals;
  });
};

export const useGetFoldersQuery = (dealId: IDeal["id"] | null | undefined) => {
  const { getDealFolders } = useTaggerQueries();

  return useQuery(
    ["deal-folders", dealId],
    () => {
      if (!dealId) {
        return Promise.resolve([]);
      }
      return getDealFolders(dealId);
    },
    {
      enabled: !!dealId
    }
  );
};

export const useGetFolderDataQuery = (folderId: number | undefined) => {
  const { getFolderData } = useTaggerQueries();

  return useQuery(
    ["folder-data", folderId],
    () => {
      if (!folderId) {
        return Promise.resolve(null);
      }
      return getFolderData(folderId);
    },
    {
      enabled: !!folderId
    }
  );
};

export const useGetDealRoundInvestorsQuery = (
  dealId: IDeal["id"] | null
): UseQueryResult<IDealRoundsWithInvestors[] | null> => {
  const { getDealRoundsWithInvestorsAction } = useDealActions();

  return useQuery(["deal-round-investors", dealId], () =>
    dealId ? getDealRoundsWithInvestorsAction({ dealId }) : Promise.resolve(null)
  );
};

export const useGetDealInvestorsQuery = (
  dealId: IDeal["id"] | null
): UseQueryResult<{ investors: IInvestor[] } | null> => {
  const { getDealInvestors } = useTaggerQueries();

  return useQuery(["deal-investors", dealId], () => (dealId ? getDealInvestors(dealId) : Promise.resolve(null)));
};
